import { Alert, Divider, Stack } from "@mui/material";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useCallback, useState } from "react";
import type { FilterWidget, FilterWidgetSettings } from "../../../../../../api/types/dashboardsTypes";
import { FilterComponentType, ReferenceDateType } from "../../../../../../api/types/dashboardsTypes";
import SwitchField from "../../../../reporting/sv/components/common/SwitchField";
import WidgetSettingsComponent from "../../../components/widgets-toolbox/WidgetSettingsComponent";
import { useDashboardContext } from "../../../contexts/DashboardContext";
import type { DashboardFilter } from "../../../dashboard.types";
import { getFilterWidgetValues } from "../getFilterWidgetValues";
import DefaultValue from "./DefaultValueComponent";
import { default as FilterComponentTypeComponent } from "./FilterComponentTypeComponent";
import LinkToParentFilter from "./LinkToParentFilter";
import SelectedFilter from "./SelectedFilter";

const defaultSettings = {
  componentType: FilterComponentType.Dropdown,
  singleSelect: true,
  defaultValue: [],
  referenceDate: ReferenceDateType.ThisMonth,
  parentWidgetId: null,
};

const getWidgetSettings = (widget: FilterWidget): FilterWidgetSettings => ({
  componentType: widget.componentType ?? defaultSettings.componentType,
  singleSelect: widget.singleSelect ?? defaultSettings.singleSelect,
  defaultValue: widget.defaultValue ?? defaultSettings.defaultValue,
  referenceDate: widget.referenceDate,
  hideTitle: widget.hideTitle,
  parentWidgetId: widget.parentWidgetId,
  hideSecondaryColumn: widget.hideSecondaryColumn,
});

interface Props {
  widget: FilterWidget;
  onSave: (widgetId: string, properties: Partial<FilterWidget>, values: unknown[]) => void;
}

function FilterWidgetSettingsComponent({ widget, onSave }: Props) {
  const { filters, dashboardLoaded } = useDashboardContext();

  const [selectedFilter, setSelectedFilter] = useState<DashboardFilter | null>(
    filters.find((f) => f.id === widget.dashboardFilterId) ?? null
  );

  const [settings, setSettings] = useState<FilterWidgetSettings>(() => getWidgetSettings(widget));

  const handleSettingsChange = useCallback((changes: Partial<FilterWidgetSettings>) => {
    setSettings((prev) => ({ ...prev, ...changes }));
  }, []);

  const handleApply = () => {
    if (!selectedFilter) {
      onSave(widget.widgetId, { ...defaultSettings, dashboardFilterId: undefined }, []);
      return;
    }
    onSave(
      widget.widgetId,
      { ...settings, dashboardFilterId: selectedFilter.id },
      getFilterWidgetValues(selectedFilter, settings)
    );
  };

  return (
    <WidgetSettingsComponent
      widget={widget}
      showApplyButton={true}
      applyDisabled={filters.length === 0 && !dashboardLoaded}
      onApply={handleApply}
    >
      <Configuration
        widgetId={widget.widgetId}
        selectedFilter={selectedFilter}
        settings={settings}
        setSelectedFilter={setSelectedFilter}
        onSettingsChange={handleSettingsChange}
      />
    </WidgetSettingsComponent>
  );
}

export default FilterWidgetSettingsComponent;

interface ConfigurationProps {
  widgetId: string;
  selectedFilter: DashboardFilter | null;
  settings: FilterWidgetSettings;
  setSelectedFilter: (filter: DashboardFilter | null) => void;
  onSettingsChange: (settings: Partial<FilterWidgetSettings>) => void;
}

const Configuration = ({
  widgetId,
  selectedFilter,
  settings,
  setSelectedFilter,
  onSettingsChange,
}: ConfigurationProps) => {
  const { filters, dashboardLoaded } = useDashboardContext();
  const isAnyFilterAvailable = filters.length > 0;
  const isLoadingFilters = !isAnyFilterAvailable && !dashboardLoaded;

  return (
    <Stack direction="column" useFlexGap spacing={2} sx={{ alignItems: "center", p: 2, flex: 1, minHeight: 0 }}>
      <SwitchField
        label="Hide Widget Title"
        checked={settings.hideTitle}
        disabled={isLoadingFilters}
        containerSx={{ width: "100%", flex: "0 0 auto" }}
        onChange={(hideTitle) => onSettingsChange({ hideTitle })}
      />
      {isAnyFilterAvailable && (
        <>
          <Divider sx={{ width: "100%", borderStyle: "dashed" }} />
          <LinkToParentFilter widgetId={widgetId} settings={settings} onSettingsChange={onSettingsChange} />
          <Divider sx={{ width: "100%", borderStyle: "dashed" }} />
          <SelectedFilter selectedFilter={selectedFilter} filters={filters} onChange={setSelectedFilter} />
          <FilterComponentTypeComponent
            key={selectedFilter?.id}
            filter={selectedFilter}
            settings={settings}
            onSettingsChange={onSettingsChange}
          />
          <DefaultValue filter={selectedFilter} settings={settings} onSettingsChange={onSettingsChange} />
        </>
      )}
      {isLoadingFilters && <InlineLoader />}
      {!isAnyFilterAvailable && !isLoadingFilters && (
        <Alert sx={{ m: 2, width: "100%" }} severity="warning">
          No filters available
        </Alert>
      )}
    </Stack>
  );
};
